import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  Divider,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowIcon from '@klokhuis/icons/arrow-icon.svg';
import { useRouter } from 'next/router';
// Removed apiPost import - now using Next.js API routes directly
import { authPalette } from '@klokhuis/modules/Auth/AuthTheme';
import AuthSocialButtons from '@klokhuis/modules/Auth/AuthSocialButtons';
import styles from './SignupSignin.module.css';
import { LogoLink } from '@klokhuis/core/PageLayout/components/Header/LogoLink';
import realEstateImage from '@klokhuis/assets/realstate-img.jpg';
import { ROUTES } from '@klokhuis/core/Routing';
import { useIntl } from '@klokhuis/core/Intl';

type FlowStep = 'initial' | 'signin' | 'signup' | 'forgot';
type ForgotStep = 'identifier' | 'otp' | 'reset' | 'done';

export default function SignupSigninPage() {
  const router = useRouter();
  const { T } = useIntl();
  const isMobile = useMediaQuery('(max-width:899px)');

  const returnUrl =
    typeof router.query.returnUrl === 'string' &&
    router.query.returnUrl.startsWith('/')
      ? router.query.returnUrl
      : '/';
  const [flowStep, setFlowStep] = useState<FlowStep>('initial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initial identifier (email/phone)
  const [identifier, setIdentifier] = useState('');

  // Sign In State
  const [signInPassword, setSignInPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Sign Up State
  const [signUpRole, setSignUpRole] = useState<'BUYER' | 'SELLER'>('BUYER');
  const [signUpName, setSignUpName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpEmailError, setSignUpEmailError] = useState('');
  const [signUpCountryCode, setSignUpCountryCode] = useState('+91');
  const [signUpMobile, setSignUpMobile] = useState('');
  const [formattedMobileNumber, setFormattedMobileNumber] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState('');
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignUpConfirmPassword, setShowSignUpConfirmPassword] =
    useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // OTP State
  const [otpStep, setOtpStep] = useState<'form' | 'otp'>('form');
  const [otp, setOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [signupKey, setSignupKey] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  // Forgot password state
  const [forgotStep, setForgotStep] = useState<ForgotStep>('identifier');
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotConfirm, setForgotConfirm] = useState('');

  // Check if user exists and route to signin or signup
  async function handleContinue() {
    setError('');
    if (!identifier.trim()) {
      setError(T('auth.error.enter_email_or_phone'));
      return;
    }
    setLoading(true);
    try {
      // Use Next.js API route which proxies to backend
      const res = await fetch('/api/auth/check-user', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to check user');
      }

      const data = await res.json();
      if (data.exists) {
        setFlowStep('signin');
      } else {
        setFlowStep('signup');
        // Pre-fill email if it's an email
        if (identifier.includes('@')) {
          setSignUpEmail(identifier.trim());
        } else {
          setSignUpMobile(identifier.trim().replace(/\D/g, ''));
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to check user. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    if (flowStep === 'initial') {
      router.back();
      return;
    }
    setFlowStep('initial');
    setError('');
    setSuccess('');
    setOtpStep('form');
    setSignInPassword('');
    setSignUpRole('BUYER');
    setSignUpName('');
    setSignUpEmail('');
    setSignUpEmailError('');
    setSignUpCountryCode('+91');
    setSignUpMobile('');
    setFormattedMobileNumber('');
    setSignUpPassword('');
    setSignUpConfirmPassword('');
    setOtp('');
    setUserId(null);
    setSignupKey(null);

    // reset forgot state
    setForgotStep('identifier');
    setForgotIdentifier('');
    setForgotOtp('');
    setForgotPassword('');
    setForgotConfirm('');
  }

  // Note: We don't check for token here because _app.tsx handles authentication
  // If user is authenticated, _app.tsx will handle redirects appropriately
  // This prevents redirect loops

  // Sign In Handler
  async function handleSignIn() {
    setError('');
    setLoading(true);
    try {
      // Use Next.js API route which sets HTTP-only cookies
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include', // Important: include cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password: signInPassword,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await res.json();

      // Store user data for profile page (tokens are in HTTP-only cookies)
      if (data.user) {
        // Use a helper function to safely store in localStorage
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('userData', JSON.stringify(data.user));
          }
        } catch (e: any) {
          // Storage access may be blocked in some contexts (iframe, privacy settings, etc.)
          // Silently fail - the app will work without localStorage
          if (e?.name !== 'SecurityError' && e?.message?.includes('storage')) {
            console.warn('Failed to store userData in localStorage:', e);
          }
        }
      }

      setSuccess(T('auth.success.login'));
      // Use window.location.href to force full page reload
      // This ensures cookies are properly read by the server on the next request
      setTimeout(() => {
        window.location.href = returnUrl;
      }, 1000);
    } catch (e: any) {
      setError(e?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  // Helper function to format mobile number with country code
  function formatMobileNumber(mobile: string): string {
    const cleanMobile = mobile.replace(/\D/g, '');
    if (cleanMobile.length < 10) return '';
    const code = signUpCountryCode.trim();
    const withPlus = code.startsWith('+')
      ? code
      : '+' + code.replace(/\D/g, '');
    return withPlus + cleanMobile;
  }

  // Sign Up - Step 1: Request OTP (user not created yet)
  async function handleSignUp() {
    setError('');
    if (!signUpRole) {
      setError(T('auth.error.role_required'));
      return;
    }
    if (signUpPassword !== signUpConfirmPassword) {
      setError(T('auth.error.passwords_no_match'));
      return;
    }
    if (signUpPassword.length < 8) {
      setError(T('auth.error.password_min_8'));
      return;
    }
    if (!signUpMobile || signUpMobile.replace(/\D/g, '').length < 10) {
      setError(T('auth.error.valid_phone'));
      return;
    }
    // Format mobile number with +91 prefix if needed
    const fullMobileNumber = formatMobileNumber(signUpMobile);
    if (!fullMobileNumber) {
      setError(T('auth.error.valid_phone_number'));
      return;
    }
    console.log(
      `[SIGNUP] User entered: ${signUpMobile}, Full number with +91: ${fullMobileNumber}`,
    );
    // Store the formatted mobile number for display in OTP screen
    setFormattedMobileNumber(fullMobileNumber);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: signUpName,
          email: signUpEmail,
          mobile: fullMobileNumber,
          password: signUpPassword,
          role: signUpRole,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      const data = await res.json();
      setSignupKey(data.signupKey);
      if (data.devOtp) {
        setEmailOtp(data.devOtp.email);
        setMobileOtp(data.devOtp.mobile);
      }
      setOtpStep('otp');
      setSuccess(T('auth.success.otp_sent', formattedMobileNumber, signUpEmail));
    } catch (e: any) {
      setError(e?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Verify OTP and Complete Signup
  async function handleVerifyOtp() {
    setError('');
    if (otp.length !== 6) {
      setError(T('auth.error.otp_6_digit'));
      return;
    }
    if (!signupKey) {
      setError(T('auth.error.session_expired'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/verify-signup-otp', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signupKey,
          otp: otp,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Invalid or expired OTP');
      }

      const data = await res.json();
      setUserId(data.userId);
      setSuccess(T('auth.success.account_created'));
      setTimeout(() => {
        setFlowStep('signin');
        setOtpStep('form');
        setIdentifier(signUpEmail);
        setSignupKey(null);
        setEmailOtp('');
        setMobileOtp('');
      }, 2000);
    } catch (e: any) {
      setError(e?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Resend OTP
  async function handleResendOtp() {
    setError('');
    setSuccess('');
    if (!signupKey) {
      setError(T('auth.error.resend_failed'));
      return;
    }
    if (!signUpMobile || signUpMobile.replace(/\D/g, '').length < 10) {
      setError(T('auth.error.valid_phone'));
      return;
    }
    // Format mobile number with +91 prefix if needed
    const fullMobileNumber = formatMobileNumber(signUpMobile);
    if (!fullMobileNumber) {
      setError(T('auth.error.valid_phone_number'));
      return;
    }
    console.log(
      `[RESEND-OTP] User entered: ${signUpMobile}, Full number with +91: ${fullMobileNumber}`,
    );
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: signUpName,
          email: signUpEmail,
          mobile: fullMobileNumber,
          password: signUpPassword,
          role: signUpRole,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to resend OTP');
      }

      const data = await res.json();
      setSignupKey(data.signupKey);
      if (data.devOtp) {
        setEmailOtp(data.devOtp.email);
        setMobileOtp(data.devOtp.mobile);
      }
      setSuccess(T('auth.success.otp_resent'));
    } catch (e: any) {
      setError(e?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Forgot password handlers
  async function handleForgotRequestOtp() {
    setError('');
    if (!forgotIdentifier.trim()) {
      setError(T('auth.error.enter_email_or_mobile'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: forgotIdentifier.trim() }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      setForgotStep('otp');
      setSuccess(T('auth.success.forgot_otp_sent'));
    } catch (e: any) {
      setError(e?.message || T('auth.success.forgot_otp_sent'));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotVerifyOtp() {
    setError('');
    setSuccess('');
    if (forgotOtp.length !== 6) {
      setError(T('auth.error.otp_6_digit'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: forgotIdentifier.trim(),
          otp: forgotOtp,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Invalid or expired OTP');
      }

      setForgotStep('reset');
      setSuccess(T('auth.success.forgot_otp_verified'));
    } catch (e: any) {
      setError(e?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotResendOtp() {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: forgotIdentifier.trim() }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to resend OTP');
      }

      setSuccess(T('auth.success.forgot_otp_resent'));
    } catch (e: any) {
      setError(e?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotResetPassword() {
    setError('');
    if (!forgotPassword || forgotPassword.length < 8) {
      setError(T('auth.error.password_min_8_long'));
      return;
    }
    if (forgotPassword !== forgotConfirm) {
      setError(T('auth.error.passwords_no_match'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: forgotIdentifier.trim(),
          otp: forgotOtp,
          newPassword: forgotPassword,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to reset password');
      }

      setForgotStep('done');
      setSuccess(T('auth.success.password_reset'));
    } catch (e: any) {
      setError(e?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Head>
        <title>{T('auth.sign_up_sign_in_title')} - Klokhuis</title>
      </Head>
      <Box
        className={styles.pageContainer}
        style={{
          backgroundColor: isMobile
            ? authPalette.white
            : authPalette.leftPanelBg,
        }}
      >
        <Box className={styles.container}>
          <Grid container spacing={0} className={styles.gridContainer}>
            {/* Left Panel - Branding */}
            <Grid
              item
              md={5}
              className={styles.leftPanel}
              style={{ backgroundColor: authPalette.leftPanelBg }}
            >
              <Button
                type="button"
                onClick={handleBack}
                className={styles.backButton}
                style={{ color: authPalette.textPrimary }}
              >
                <div style={{ width: 18, height: 18 }}>
                  <ArrowIcon />
                </div>{' '}
                <Typography variant="body1">{T('auth.back')}</Typography>
              </Button>
              <Box className={styles.leftPanelContent}>
                {/* Logo */}
                <Box className={styles.logoContainer}>
                  <Box className={styles.logoLinkContainer}>
                    <LogoLink />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    className={styles.logoSubtitle}
                  >
                    {T('auth.your_property_partner')}
                  </Typography>
                </Box>
                <Typography variant="h3">
                  {flowStep === 'signup'
                    ? T('auth.start_your_journey')
                    : T('auth.welcome_back')}
                </Typography>

                {/* Branding Illustration */}
                <Box className={styles.brandingImageContainer}>
                  <Box
                    component="img"
                    src={realEstateImage.src}
                    alt="Klokhuis Branding"
                    className={styles.brandingImage}
                  />
                </Box>

                <Typography
                  variant="subtitle1"
                  className={styles.welcomeDescription}
                >
                  {flowStep === 'signup'
                    ? T('auth.signup_tagline')
                    : T('auth.signin_tagline')}
                </Typography>
              </Box>
            </Grid>

            {/* Right Panel - Form */}
            <Grid
              item
              md={7}
              xs={12}
              className={styles.rightPanel}
              style={{
                backgroundColor: isMobile
                  ? authPalette.white
                  : authPalette.formBg,
              }}
            >
              {/* Mobile: back on every step (initial → browser back) */}
              {isMobile && (
                <Button
                  type="button"
                  onClick={handleBack}
                  className={styles.backButtonMobile}
                >
                  <div style={{ width: 18, height: 18 }}>
                    <ArrowIcon />
                  </div>
                  <span className={styles.backButtonLabel}>{T('auth.back_btn')}</span>
                </Button>
              )}
              <Box className={styles.formContainer}>
                {error && (
                  <Alert
                    severity="error"
                    className={styles.errorAlert}
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert
                    severity="success"
                    className={styles.successAlert}
                    onClose={() => setSuccess('')}
                  >
                    {success}
                  </Alert>
                )}

                {/* Initial Step - Enter Email/Phone */}
                {flowStep === 'initial' && (
                  <Stack
                    spacing={isMobile ? 3 : 8}
                    className={isMobile ? styles.mobileAuthStack : undefined}
                  >
                    <Box className={styles.mobileLogoContainer}>
                      <LogoLink />
                    </Box>

                    <Box className={styles.formTitle}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        component="h1"
                        className={styles.authFormHeadingDesktopGap}
                      >
                        {T('auth.sign_up_sign_in_title')}
                      </Typography>

                      <Typography
                        variant={isMobile ? 'caption' : 'subtitle2'}
                        className={styles.mobileAuthSubtitle}
                        style={{ color: authPalette.textSecondary }}
                      >
                        {T('auth.enter_details_subtitle')}
                      </Typography>
                    </Box>
                    <TextField
                      id="initial-identifier"
                      label={
                        isMobile
                          ? T('auth.email_or_phone_label_mobile')
                          : T('auth.email_or_phone_label_desktop')
                      }
                      value={identifier}
                      className={styles.formInput}
                      onChange={e =>
                        setIdentifier(e.target.value.toLocaleLowerCase())
                      }
                      fullWidth
                      required
                      variant="outlined"
                      placeholder={
                        isMobile
                          ? T('auth.email_or_phone_placeholder_mobile')
                          : T('auth.email_or_phone_placeholder_desktop')
                      }
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleContinue();
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleContinue}
                      disabled={loading}
                      // className={styles.authFormPrimaryBtn}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        T('auth.continue')
                      )}
                    </Button>
                  </Stack>
                )}

                {/* Sign In Form */}
                {flowStep === 'signin' && otpStep === 'form' && (
                  <Stack
                    spacing={isMobile ? 3 : 4}
                    className={isMobile ? styles.mobileAuthStack : undefined}
                  >
                    <Box className={styles.mobileLogoContainer}>
                      <LogoLink />
                    </Box>

                    <Box className={styles.formSubtitle}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        component="h2"
                        className={`${styles.formTitle} ${styles.authFormHeadingDesktopGap}`}
                      >
                        {T('auth.sign_in_title')}
                      </Typography>
                      <Typography
                        variant={isMobile ? 'caption' : 'subtitle2'}
                        className={styles.signupSubtitle}
                        style={{ color: authPalette.textSecondary }}
                      >
                        {T('auth.enter_details_subtitle')}
                      </Typography>
                    </Box>
                    <TextField
                      id="signin-identifier"
                      label={T('auth.email_or_phone_label')}
                      value={identifier}
                      disabled
                      fullWidth
                      variant="outlined"
                      // InputProps={{
                      //   startAdornment: (
                      //     <InputAdornment position="start">
                      //       <Typography className={styles.countryCodeText}>
                      //         +91
                      //       </Typography>
                      //     </InputAdornment>
                      //   ),
                      // }}
                    />
                    <TextField
                      id="signin-password"
                      label={T('auth.password_label')}
                      type={showPassword ? 'text' : 'password'}
                      value={signInPassword}
                      onChange={e => setSignInPassword(e.target.value)}
                      onKeyPress={e => {
                        if (
                          e.key === 'Enter' &&
                          identifier &&
                          signInPassword &&
                          !loading
                        ) {
                          handleSignIn();
                        }
                      }}
                      fullWidth
                      required
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            className={styles.passwordInputAdornment}
                            position="end"
                          >
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSignIn}
                      disabled={loading || !identifier || !signInPassword}
                      className={styles.authFormPrimaryBtn}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        T('auth.sign_in_btn')
                      )}
                    </Button>
                    <Box className={styles.forgotPasswordLink}>
                      <Link
                        href="#"
                        variant="body1"
                        onClick={e => {
                          e.preventDefault();
                          setFlowStep('forgot');
                          setForgotStep('identifier');
                          setForgotIdentifier(identifier);
                          setError('');
                          setSuccess('');
                        }}
                      >
                        {T('auth.forgot_password')}
                      </Link>
                    </Box>
                  </Stack>
                )}

                {/* Forgot Password Flow */}
                {flowStep === 'forgot' && (
                  <Stack
                    spacing={isMobile ? 3 : 4}
                    className={isMobile ? styles.mobileAuthStack : undefined}
                  >
                    <Box className={styles.mobileLogoContainer}>
                      <LogoLink />
                    </Box>

                    {forgotStep === 'identifier' && (
                      <>
                        <Box className={styles.formSubtitle}>
                          <Typography
                            variant={isMobile ? 'h6' : 'h4'}
                            component="h2"
                            className={`${styles.formTitle} ${styles.authFormHeadingDesktopGap}`}
                          >
                            {T('auth.forgot_password_title')}
                          </Typography>
                          <Typography
                            variant={isMobile ? 'caption' : 'subtitle2'}
                            className={styles.signupSubtitle}
                            style={{ color: authPalette.textSecondary }}
                          >
                            {T('auth.forgot_password_subtitle')}
                          </Typography>
                        </Box>
                        <TextField
                          id="forgot-identifier"
                          label={T('auth.email_or_mobile_label')}
                          value={forgotIdentifier}
                          onChange={e => setForgotIdentifier(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          onKeyPress={e => {
                            if (e.key === 'Enter' && forgotIdentifier.trim()) {
                              handleForgotRequestOtp();
                            }
                          }}
                        />

                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleForgotRequestOtp}
                          disabled={loading || !forgotIdentifier.trim()}
                          className={styles.authFormPrimaryBtn}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            T('auth.send_otp')
                          )}
                        </Button>
                      </>
                    )}

                    {forgotStep === 'otp' && (
                      <>
                        <Box className={styles.formSubtitle}>
                          <Typography
                            variant={isMobile ? 'h6' : 'h4'}
                            component="h2"
                            className={`${styles.formTitle} ${styles.authFormHeadingDesktopGap}`}
                          >
                            {T('auth.verify_otp_title')}
                          </Typography>
                          <Typography
                            variant={isMobile ? 'caption' : 'subtitle2'}
                            className={styles.signupSubtitle}
                            style={{ color: authPalette.textSecondary }}
                          >
                            {T('auth.otp_sent_to', forgotIdentifier)}
                          </Typography>
                        </Box>
                        <TextField
                          id="forgot-otp"
                          label={T('auth.otp_label')}
                          value={forgotOtp}
                          onChange={e =>
                            setForgotOtp(
                              e.target.value.replace(/\D/g, '').slice(0, 6),
                            )
                          }
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            maxLength: 6,
                          }}
                          fullWidth
                          variant="outlined"
                          onKeyPress={e => {
                            if (e.key === 'Enter' && forgotOtp.length === 6) {
                              handleForgotVerifyOtp();
                            }
                          }}
                        />
                        <Box className={styles.resendOtpDiv}>
                          <Typography
                            variant="subtitle1"
                            className={styles.resendOtpTextStyle}
                            onClick={handleForgotResendOtp}
                          >
                            {T('auth.resend_otp')}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleForgotVerifyOtp}
                          disabled={loading || forgotOtp.length !== 6}
                          className={styles.authFormPrimaryBtn}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            T('auth.verify_otp_btn')
                          )}
                        </Button>
                        {/* <Button
                          variant="text"
                          onClick={handleForgotResendOtp}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Typography variant="body1">Resend OTP</Typography>
                          )}
                        </Button> */}
                      </>
                    )}

                    {forgotStep === 'reset' && (
                      <>
                        <Box className={styles.formSubtitle}>
                          <Typography
                            variant={isMobile ? 'h6' : 'h4'}
                            component="h2"
                            className={`${styles.formTitle} ${styles.authFormHeadingDesktopGap}`}
                          >
                            {T('auth.reset_password_title')}
                          </Typography>
                          <Typography
                            variant={isMobile ? 'caption' : 'subtitle2'}
                            className={styles.signupSubtitle}
                            style={{ color: authPalette.textSecondary }}
                          >
                            {T('auth.reset_password_subtitle')}
                          </Typography>
                        </Box>
                        <TextField
                          id="forgot-new-password"
                          label={T('auth.new_password_label')}
                          type="password"
                          value={forgotPassword}
                          onChange={e => setForgotPassword(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          helperText={T('auth.min_8_chars')}
                        />
                        <TextField
                          id="forgot-confirm-password"
                          label={T('auth.confirm_password_label')}
                          type="password"
                          value={forgotConfirm}
                          onChange={e => setForgotConfirm(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          onKeyPress={e => {
                            if (
                              e.key === 'Enter' &&
                              forgotPassword &&
                              forgotPassword === forgotConfirm
                            ) {
                              handleForgotResetPassword();
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleForgotResetPassword}
                          disabled={
                            loading ||
                            !forgotPassword ||
                            forgotPassword !== forgotConfirm ||
                            forgotPassword.length < 8
                          }
                          className={styles.authFormPrimaryBtn}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            T('auth.reset_password_btn')
                          )}
                        </Button>
                      </>
                    )}

                    {forgotStep === 'done' && (
                      <>
                        <Box className={styles.formSubtitle}>
                          <Typography
                            variant={isMobile ? 'h6' : 'h4'}
                            component="h2"
                            className={`${styles.formTitle} ${styles.authFormHeadingDesktopGap}`}
                          >
                            {T('auth.password_reset_success_title')}
                          </Typography>
                          <Typography
                            variant={isMobile ? 'caption' : 'subtitle2'}
                            className={styles.signupSubtitle}
                            style={{ color: authPalette.textSecondary }}
                          >
                            {T('auth.password_reset_success_subtitle')}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            setFlowStep('signin');
                            setForgotStep('identifier');
                            setForgotOtp('');
                            setForgotPassword('');
                            setForgotConfirm('');
                            setSuccess('');
                          }}
                          className={styles.authFormPrimaryBtn}
                        >
                          {T('auth.go_to_sign_in')}
                        </Button>
                      </>
                    )}
                  </Stack>
                )}

                {/* Sign Up Form */}
                {flowStep === 'signup' && otpStep === 'form' && (
                  <Stack
                    spacing={isMobile ? 0.5 : 2.5}
                    className={`${styles.signupStack} ${
                      isMobile ? styles.mobileAuthStack : ''
                    }`}
                  >
                    <Box className={styles.mobileLogoContainer}>
                      <LogoLink />
                    </Box>
                    <Box className={styles.formSubtitle}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        component="h2"
                        className={`${styles.signupTitle} ${styles.authFormHeadingDesktopGap}`}
                      >
                        {T('auth.sign_up_title')}
                      </Typography>
                      <Typography
                        variant={isMobile ? 'caption' : 'subtitle2'}
                        className={styles.signupSubtitle}
                        style={{ color: authPalette.textSecondary }}
                      >
                        {T('auth.enter_details_subtitle')}
                      </Typography>
                    </Box>
                    {/* Role Selector */}
                    <Box>
                      <Typography
                        variant="body4"
                        className={styles.roleSelectorLabel}
                        style={{ color: authPalette.textPrimary }}
                      >
                        {T('auth.i_want_to_join_as')}
                      </Typography>
                      <Box className={styles.roleButtonsContainer}>
                        {[
                          { value: 'BUYER' as const, label: T('auth.buyer') },
                          { value: 'SELLER' as const, label: T('auth.seller') },
                        ].map(({ value, label }) => (
                          <Button
                            key={value}
                            variant={
                              signUpRole === value ? 'contained' : 'outlined'
                            }
                            onClick={() => setSignUpRole(value)}
                            className={`${styles.roleButton} ${
                              signUpRole === value
                                ? styles.roleButtonActive
                                : ''
                            }`}
                            style={{
                              borderColor: authPalette.borderLight,
                              color:
                                signUpRole === value
                                  ? authPalette.white
                                  : authPalette.textPrimary,
                              backgroundColor:
                                signUpRole === value
                                  ? authPalette.primaryDark
                                  : 'transparent',
                            }}
                            onMouseEnter={e => {
                              if (signUpRole !== value) {
                                e.currentTarget.style.backgroundColor =
                                  authPalette.hoverBg;
                                e.currentTarget.style.borderColor =
                                  authPalette.borderLight;
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  authPalette.primaryDarkHover;
                                e.currentTarget.style.borderColor =
                                  authPalette.primaryDark;
                              }
                            }}
                            onMouseLeave={e => {
                              if (signUpRole !== value) {
                                e.currentTarget.style.backgroundColor =
                                  'transparent';
                                e.currentTarget.style.borderColor =
                                  authPalette.borderLight;
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  authPalette.primaryDarkHover;
                                e.currentTarget.style.borderColor =
                                  authPalette.primaryDark;
                              }
                            }}
                          >
                            {label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                    <TextField
                      id="signup-name"
                      label={T('auth.full_name_label')}
                      placeholder={T('auth.enter_name_placeholder')}
                      value={signUpName}
                      onChange={e => setSignUpName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      className={styles.signupField}
                    />
                    <TextField
                      id="signup-email"
                      label={T('auth.email_label')}
                      type="email"
                      placeholder={T('auth.enter_email_placeholder')}
                      value={signUpEmail}
                      onChange={e => {
                        const value = e.target.value;
                        setSignUpEmail(value);
                        if (value && !validateEmail(value)) {
                          setSignUpEmailError(T('auth.error.valid_email'));
                        } else {
                          setSignUpEmailError('');
                        }
                      }}
                      onBlur={() => {
                        if (signUpEmail && !validateEmail(signUpEmail)) {
                          setSignUpEmailError(T('auth.error.valid_email'));
                        } else {
                          setSignUpEmailError('');
                        }
                      }}
                      error={!!signUpEmailError}
                      helperText={signUpEmailError}
                      fullWidth
                      required
                      variant="outlined"
                      className={styles.signupField}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        component="label"
                        htmlFor="signup-phone"
                        className={styles.phoneLabel}
                        style={{ color: authPalette.textPrimary }}
                      >
                        {T('auth.phone_number_label')}
                      </Typography>
                      <Box className={styles.phoneRow}>
                        <TextField
                          id="signup-country-code"
                          value="(+91)"
                          disabled
                          variant="outlined"
                          className={styles.phoneCountryCode}
                          inputProps={{
                            'aria-label': 'Country code',
                            style: { textAlign: 'center' },
                          }}
                        />
                        <TextField
                          id="signup-mobile"
                          placeholder="9999999999"
                          value={signUpMobile}
                          onChange={e =>
                            setSignUpMobile(
                              e.target.value.replace(/\D/g, '').slice(0, 10),
                            )
                          }
                          fullWidth
                          required
                          variant="outlined"
                          className={styles.phoneNumber}
                          inputProps={{
                            maxLength: 10,
                            inputMode: 'numeric',
                          }}
                        />
                      </Box>
                    </Box>
                    <TextField
                      id="signup-password"
                      label={T('auth.password_label')}
                      type={showSignUpPassword ? 'text' : 'password'}
                      placeholder="••••••••••••"
                      value={signUpPassword}
                      onChange={e => setSignUpPassword(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      className={styles.signupField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            className={styles.passwordInputAdornment}
                            position="end"
                          >
                            <IconButton
                              onClick={() =>
                                setShowSignUpPassword(!showSignUpPassword)
                              }
                              edge="end"
                            >
                              {showSignUpPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      id="signup-confirm-password"
                      label={T('auth.confirm_password_label')}
                      type={showSignUpConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••••••"
                      value={signUpConfirmPassword}
                      onChange={e => setSignUpConfirmPassword(e.target.value)}
                      onKeyPress={e => {
                        if (
                          e.key === 'Enter' &&
                          signUpRole &&
                          signUpName &&
                          signUpEmail &&
                          signUpMobile &&
                          signUpPassword &&
                          signUpConfirmPassword &&
                          !loading
                        ) {
                          handleSignUp();
                        }
                      }}
                      fullWidth
                      required
                      variant="outlined"
                      className={styles.signupField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            className={styles.passwordInputAdornment}
                            position="end"
                          >
                            <IconButton
                              onClick={() =>
                                setShowSignUpConfirmPassword(
                                  !showSignUpConfirmPassword,
                                )
                              }
                              edge="end"
                            >
                              {showSignUpConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box className={styles.termsBlock}>
                      <Typography variant={isMobile ? 'caption' : 'subtitle2'}>
                        {T('auth.terms_agree')}{' '}
                        <Link
                          href={ROUTES.TermsAndConditions.url({})}
                          className={styles.footerLink}
                          style={{ color: authPalette.primaryDark }}
                          target="_blank"
                        >
                          {T('auth.terms_conditions')}
                        </Link>
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSignUp}
                      disabled={
                        loading ||
                        !signUpRole ||
                        !signUpName ||
                        !signUpEmail ||
                        !signUpMobile ||
                        !signUpPassword ||
                        !signUpConfirmPassword
                      }
                      className={styles.authFormPrimaryBtn}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        T('auth.sign_up_btn')
                      )}
                    </Button>
                    <Box className={styles.footerText}>
                      <Typography variant={isMobile ? 'caption' : 'subtitle2'}>
                        {T('auth.already_have_account')}{' '}
                        <Link
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            setFlowStep('signin');
                          }}
                          className={styles.footerLink}
                          style={{ color: authPalette.primaryDark }}
                        >
                          {T('auth.sign_in_link')}
                        </Link>
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {/* OTP Verification Step */}
                {flowStep === 'signup' && otpStep === 'otp' && (
                  <Stack
                    spacing={isMobile ? 3 : 3}
                    className={isMobile ? styles.mobileAuthStack : undefined}
                  >
                    <Box className={styles.mobileLogoContainer}>
                      <LogoLink />
                    </Box>

                    <Box className={styles.checkmarkBadgeContainer}>
                      <img src="/checkmark-badge.svg" alt="check mark badge" />
                    </Box>

                    <Box className={styles.contentContainer}>
                      <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        component="h2"
                        className={`${styles.otpTitle} ${styles.authFormHeadingDesktopGap}`}
                      >
                        {T('auth.verify_account_title')}
                      </Typography>
                      <Typography
                        variant={isMobile ? 'caption' : 'subtitle2'}
                        color="text.secondary"
                        className={styles.otpDescription}
                      >
                        {T('auth.verify_account_subtitle', formattedMobileNumber, signUpEmail)}
                      </Typography>
                    </Box>
                    <TextField
                      id="signup-otp"
                      label={T('auth.enter_otp_label')}
                      value={otp}
                      onChange={e =>
                        setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      onKeyPress={e => {
                        if (e.key === 'Enter' && otp.length === 6 && !loading) {
                          handleVerifyOtp();
                        }
                      }}
                      fullWidth
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 6,
                      }}
                      variant="outlined"
                      className={styles.otpInputField}
                    />
                    <Box className={styles.resendOtpDiv}>
                      <Typography
                        variant="subtitle1"
                        className={styles.resendOtpTextStyle}
                        onClick={handleResendOtp}
                      >
                        {T('auth.resend_otp')}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleVerifyOtp}
                      disabled={loading || otp.length !== 6}
                      className={styles.authFormPrimaryBtn}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        T('auth.verify_otp_btn')
                      )}
                    </Button>

                    <Typography
                      variant="subtitle2"
                      className={styles.backToSignUpTextStyle}
                      onClick={() => setOtpStep('form')}
                    >
                      {T('auth.back_to_signup')}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}
